import { createContext, useContext, useReducer } from 'react';

const initialState = {
  status: 'idle', // idle | countdown | playing | paused | finished
  mode: 'local', // local | multiplayer | training
  score: 0,
  currentWordIndex: 0,
  passesLeft: 3,
  words: [],
  wordsGuessed: 0,
  wrongAnswers: 0,
  wordsHistory: [], // [{word, result: 'correct'|'wrong'|'pass'}]
};

export const ACTIONS = {
  START_GAME: 'START_GAME',
  COUNTDOWN: 'COUNTDOWN',
  PAUSE_GAME: 'PAUSE_GAME',
  RESUME_GAME: 'RESUME_GAME',
  END_GAME: 'END_GAME',
  CORRECT_GUESS: 'CORRECT_GUESS',
  WRONG_GUESS: 'WRONG_GUESS',
  PASS_WORD: 'PASS_WORD',
  NEXT_WORD: 'NEXT_WORD',
  SET_WORDS: 'SET_WORDS',
  SET_MODE: 'SET_MODE',
  SYNC_STATE: 'SYNC_STATE',
  RESET: 'RESET',
  REPORTED_WORD: 'REPORTED_WORD',
};

const gameReducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.RESET:
      return { ...initialState, mode: state.mode };

    case ACTIONS.SET_MODE:
      return { ...state, mode: action.payload };

    case ACTIONS.SET_WORDS:
      return { ...state, words: action.payload };

    case ACTIONS.SYNC_STATE:
      return { ...state, ...action.payload };

    case ACTIONS.COUNTDOWN:
      return { ...state, status: 'countdown' };

    case ACTIONS.START_GAME:
      return {
        ...initialState,
        mode: state.mode,
        words: state.words,
        status: 'playing',
      };

    case ACTIONS.PAUSE_GAME:
      return { ...state, status: 'paused' };

    case ACTIONS.RESUME_GAME:
      return { ...state, status: 'playing' };

    case ACTIONS.END_GAME:
      return { ...state, status: 'finished' };

    case ACTIONS.NEXT_WORD:
      return {
        ...state,
        currentWordIndex: state.currentWordIndex + 1,
      };

    case ACTIONS.CORRECT_GUESS: {
      const wordObj = state.words[state.currentWordIndex];
      const word = wordObj?.text || '';
      return {
        ...state,
        score: state.score + 1,
        wordsGuessed: state.wordsGuessed + 1,
        currentWordIndex: state.currentWordIndex + 1,
        wordsHistory: [...state.wordsHistory, { word, result: 'correct' }],
      };
    }

    case ACTIONS.WRONG_GUESS: {
      const wordObj = state.words[state.currentWordIndex];
      const word = wordObj?.text || '';
      return {
        ...state,
        score: Math.max(0, state.score - 1),
        wrongAnswers: state.wrongAnswers + 1,
        currentWordIndex: state.currentWordIndex + 1,
        wordsHistory: [...state.wordsHistory, { word, result: 'wrong' }],
      };
    }

    case ACTIONS.PASS_WORD: {
      if (state.passesLeft <= 0) return state;
      const wordObj = state.words[state.currentWordIndex];
      const word = wordObj?.text || '';
      return {
        ...state,
        passesLeft: state.passesLeft - 1,
        currentWordIndex: state.currentWordIndex + 1,
        wordsHistory: [...state.wordsHistory, { word, result: 'pass' }],
      };
    }

    case ACTIONS.REPORTED_WORD: {
      return {
        ...state,
        currentWordIndex: state.currentWordIndex + 1,
        status: 'playing',
      };
    }

    default:
      return state;
  }
};

const GameContext = createContext();

export const GameProvider = ({ children }) => {
  const [state, dispatch] = useReducer(gameReducer, initialState);
  return <GameContext.Provider value={{ state, dispatch }}>{children}</GameContext.Provider>;
};

export const useGame = () => {
  const ctx = useContext(GameContext);
  if (!ctx) throw new Error('useGame must be used within a GameProvider');
  return ctx;
};
